import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search,
  Filter,
  User,
  Users,
  Wrench,
  Settings,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [napBoxes, setNapBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [ticketsRes, techsRes, napRes] = await Promise.all([
        axios.get(`${API}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/technicians`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/nap-boxes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTickets(ticketsRes.data);
      setTechnicians(techsRes.data);
      setNapBoxes(napRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateStatus(ticket.status);
    setRemarks(ticket.remarks || "");
    setSelectedTechs(ticket.assigned_technicians || []);
    setOpen(true);
  };

  const handleTechToggle = (techId) => {
    setSelectedTechs((prev) => {
      if (prev.includes(techId)) {
        return prev.filter((id) => id !== techId);
      } else {
        // Allow max 2 technicians
        if (prev.length >= 2) {
          toast.error("Maximum 2 technicians can be assigned");
          return prev;
        }
        return [...prev, techId];
      }
    });
  };

  // Date formatting helper - MMM-dd-yyyy format (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem("token");
      const updateData = {
        status: updateStatus,
        remarks,
        assigned_technicians: selectedTechs,
      };

      await axios.put(`${API}/tickets/${selectedTicket.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ticket updated successfully");
      setOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ticket");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Open: "bg-blue-100 text-blue-700 border-blue-300",
      Assigned: "bg-purple-100 text-purple-700 border-purple-300",
      Ongoing: "bg-amber-100 text-amber-700 border-amber-300",
      Done: "bg-emerald-100 text-emerald-700 border-emerald-300",
      Closed: "bg-slate-100 text-slate-700 border-slate-300",
    };
    return (
      <span
        className={`px-2 sm:px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Open}`}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      repair: "bg-orange-100 text-orange-700",
      installation: "bg-blue-100 text-blue-700",
    };
    const icons = {
      repair: <Wrench className="w-3 h-3" />,
      installation: <Settings className="w-3 h-3" />,
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${colors[type] || colors.repair}`}
      >
        {icons[type]}
        {type?.charAt(0).toUpperCase() + type?.slice(1)}
      </span>
    );
  };

  // Filter technicians by ticket type
  const getAvailableTechs = (ticketType) => {
    return technicians.filter((tech) => {
      if (ticketType === "repair") {
        return (
          tech.technician_type === "repair" || tech.technician_type === "both"
        );
      } else if (ticketType === "installation") {
        return (
          tech.technician_type === "installation" ||
          tech.technician_type === "both"
        );
      }
      return true;
    });
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ticket.account_number || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || ticket.status === statusFilter;
    const matchesType =
      typeFilter === "all" || ticket.ticket_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, typeFilter]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1
            className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1"
            data-testid="tickets-title"
          >
            Ticket Management
          </h1>
          <p className="text-sm text-slate-600">
            View and manage all support tickets
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                {tickets.filter((t) => t.status === "Open").length}
              </div>
              <div className="text-xs text-slate-600">Open</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                {tickets.filter((t) => t.status === "Assigned").length}
              </div>
              <div className="text-xs text-slate-600">Assigned</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-600">
                {tickets.filter((t) => t.status === "Ongoing").length}
              </div>
              <div className="text-xs text-slate-600">Ongoing</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-600">
                {tickets.filter((t) => t.status === "Done").length}
              </div>
              <div className="text-xs text-slate-600">Done</div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search by ticket #, client name, or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-lg bg-white border-slate-200"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <Filter className="w-4 h-4 mr-1" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Assigned">Assigned</SelectItem>
                <SelectItem value="Ongoing">Ongoing</SelectItem>
                <SelectItem value="Done">Done</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
                <SelectItem value="installation">Installation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tickets-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">
                    Client
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden md:table-cell">
                    Type
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden lg:table-cell">
                    Date Created
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedTickets.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      {searchQuery ||
                      statusFilter !== "all" ||
                      typeFilter !== "all"
                        ? "No tickets match your filters"
                        : "No tickets yet"}
                    </td>
                  </tr>
                ) : (
                  paginatedTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      data-testid={`ticket-row-${ticket.id}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-3 sm:px-6 py-3">
                        <p className="text-xs font-mono text-slate-500">
                          {ticket.ticket_number}
                        </p>
                        <p
                          className="text-sm font-medium text-slate-900 truncate max-w-[150px]"
                          title={ticket.subject}
                        >
                          {ticket.subject}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          {ticket.client_name}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          {formatDate(ticket.created_at)}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell">
                        <p className="text-sm font-semibold text-slate-900">
                          {ticket.client_name}
                        </p>
                        {ticket.account_number && (
                          <p className="text-xs text-slate-500">
                            {ticket.account_number}
                          </p>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden md:table-cell">
                        {getTypeBadge(ticket.ticket_type || "repair")}
                      </td>
                      <td className="px-3 sm:px-6 py-3">
                        {getStatusBadge(ticket.status)}
                      </td>
                      <td className="px-3 sm:px-6 py-3">
                        {ticket.assigned_technician_names &&
                        ticket.assigned_technician_names.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {ticket.assigned_technician_names.join(", ")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-sm text-slate-500 hidden lg:table-cell">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openUpdateDialog(ticket)}
                          className="hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                          data-testid={`update-ticket-${ticket.id}`}
                        >
                          Manage
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between bg-slate-50">
              <p className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredTickets.length)}{" "}
                of {filteredTickets.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Update Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden"
            data-testid="ticket-dialog"
          >
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle className="text-xl font-bold">
                Manage Ticket: {selectedTicket?.ticket_number}
              </DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6">
              {selectedTicket && (
                <div className="space-y-6">
                  {/* Ticket Info */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Client:</span>
                        <p className="font-semibold">
                          {selectedTicket.client_name}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Account #:</span>
                        <p className="font-semibold">
                          {selectedTicket.account_number || "-"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Type:</span>
                        <p className="mt-1">
                          {getTypeBadge(selectedTicket.ticket_type || "repair")}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Created:</span>
                        <p className="font-semibold">
                          {formatDate(selectedTicket.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="text-slate-500 text-sm">Subject:</span>
                      <p className="font-semibold">{selectedTicket.subject}</p>
                    </div>
                    <div className="mt-2">
                      <span className="text-slate-500 text-sm">
                        Description:
                      </span>
                      <p className="text-sm text-slate-700">
                        {selectedTicket.description}
                      </p>
                    </div>
                    {selectedTicket.nap_box_name && (
                      <div className="mt-2">
                        <span className="text-slate-500 text-sm">NAP Box:</span>
                        <p className="font-semibold">
                          {selectedTicket.nap_box_name}{" "}
                          {selectedTicket.port_number
                            ? `Port #${selectedTicket.port_number}`
                            : ""}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Assign Technicians */}
                  <div>
                    <Label className="text-sm font-semibold">
                      Assign Technicians (max 2)
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Select technicians to assign to this ticket
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {getAvailableTechs(
                        selectedTicket.ticket_type || "repair",
                      ).map((tech) => (
                        <div
                          key={tech.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedTechs.includes(tech.id)
                              ? "border-blue-500 bg-blue-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => handleTechToggle(tech.id)}
                          data-testid={`tech-option-${tech.id}`}
                        >
                          <Checkbox
                            checked={selectedTechs.includes(tech.id)}
                            onCheckedChange={() => handleTechToggle(tech.id)}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {tech.full_name}
                            </p>
                            <p className="text-xs text-slate-500 capitalize">
                              {tech.technician_type}
                            </p>
                          </div>
                          <User className="w-4 h-4 text-slate-400" />
                        </div>
                      ))}

                      {getAvailableTechs(selectedTicket.ticket_type || "repair")
                        .length === 0 && (
                        <p className="text-sm text-slate-500 col-span-2">
                          No technicians available for this ticket type
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <Label className="text-sm font-semibold">Status</Label>
                    <Select
                      value={updateStatus}
                      onValueChange={setUpdateStatus}
                    >
                      <SelectTrigger
                        className="mt-1.5"
                        data-testid="ticket-status-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="Assigned">Assigned</SelectItem>
                        <SelectItem value="Ongoing">Ongoing</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Remarks */}
                  <div>
                    <Label className="text-sm font-semibold">Remarks</Label>
                    <Textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={3}
                      className="mt-1.5"
                      placeholder="Add remarks or notes..."
                      data-testid="ticket-remarks-input"
                    />
                  </div>

                  <Button
                    onClick={handleUpdate}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="ticket-update-btn"
                  >
                    {loading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {loading ? "Saving..." : "Update Ticket"}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Results count */}
        <div className="mt-4 text-sm text-slate-600">
          Showing {filteredTickets.length} of {tickets.length} tickets
        </div>
      </div>
    </AdminLayout>
  );
}
